import {
  IPlan,
  IPlanGeneralInfo,
  IPlanGeneralInfoPut,
  IPlanDetails,
  IPlanDetailsPut,
  IPlanElevations,
  IPlanElevationsPut,
  IPlanRooms,
  IPlanElevationsList,
  IPlanBrochureCreate,
  IPlanOvenLayout
} from '../models/plans';
import { IResponse, IDropdownResponse } from './../models/util';
import axios from '../helpers/axios'
import { logError } from '../services';

const baseUrl = '/api/plans';

export const getPlans = async (filters?: {
  name?: string;
  showArchived?: boolean;
  hasSpecs?: boolean;
  neighborhoodId?: string | number;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
}): Promise<IResponse<IPlan[]>> => {
  try {
    const { data } = await axios.get(baseUrl, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlans failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanGeneralInfo = async (planId: number | string): Promise<IPlanGeneralInfo> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/general-info`);
    return data;
  } catch (error) {
    logError(error, 'getPlanGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanDetails = async (planId: number | string): Promise<IPlanDetails> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/details`);
    return data;
  } catch (error) {
    logError(error, 'getPlanDetails failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanElevations = async (planId: number | string): Promise<IPlanElevations[]> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/elevations`);
    return data;
  } catch (error) {
    logError(error, 'getPlanElevations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanElevation = async (planElevationId: number): Promise<IPlanElevations> => {
  try {
    const { data } = await axios.get(`/api/plans/elevations/${planElevationId}`);
    return data;
  } catch (error) {
    logError(error, 'getPlanElevation failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanElevationsList = async (
  planId: number | string
): Promise<IPlanElevationsList[]> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/elevations/list`);
    return data;
  } catch (error) {
    logError(error, 'getPlanElevations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getElevationOptions = async (
  planId: number | string
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/elevations/options`);
    return data;
  } catch (error) {
    logError(error, 'getPlanElevationOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanRooms = async (planId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`/api/plans/${planId}/rooms`);
    return data;
  } catch (error) {
    logError(error, 'getPlanRooms failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFacadeTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/facade-types`);
    return data;
  } catch (error) {
    logError(error, 'getFacadeTypes failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getCladdingVersions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/cladding-versions`);
    return data;
  } catch (error) {
    logError(error, 'getPlanCladdingVersions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAppraisalAlarms = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/appraisal-alarms`);
    return data;
  } catch (error) {
    logError(error, 'getAppraisalAlarms failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getWallThicknesses = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/wall-thicknesses`);
    return data;
  } catch (error) {
    logError(error, 'getWallThicknesses failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getShowerStyles = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/shower-styles`);
    return data;
  } catch (error) {
    logError(error, 'getShowerStyles failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getStairStyles = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/stair-styles`);
    return data;
  } catch (error) {
    logError(error, 'getStairStyles failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getBedBaths = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/bed-baths`);
    return data;
  } catch (error) {
    logError(error, 'getBedBaths failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getStructuralOptions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/structural-options`);
    return data;
  } catch (error) {
    logError(error, 'getStructuralOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFramingLevels = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/framing-levels`);
    return data;
  } catch (error) {
    logError(error, 'getFramingLevels failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getParkingOptions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/parking-options`);
    return data;
  } catch (error) {
    logError(error, 'getParkingOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanRoomOptions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/plans/plan-rooms`);
    return data;
  } catch (error) {
    logError(error, 'getPlanRoomOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPlanGeneralInfo = async (payload: IPlanGeneralInfoPut): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/plans/`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'createPlansGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPlanElevation = async (
  planId: number | string,
  facadeType: string
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/plans/${planId}/elevations`, { facadeType });
    return data;
  } catch (error) {
    logError({ error }, 'createPlanElevation failed');
    return Promise.reject(error?.response?.data);
  }
};

export const addPlanRooms = async (
  planId: number | string,
  planRoomIds: IPlanRooms
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/plans/${planId}/rooms`, planRoomIds);
    return data;
  } catch (error) {
    logError({ error }, 'addPlanRooms failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putPlanGeneralInfo = async (
  planId: number | string,
  payload: IPlanGeneralInfoPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/plans/${planId}/general-info`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putPlanGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putPlanDetails = async (
  planId: number | string,
  payload: IPlanDetailsPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/plans/${planId}/details`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putPlanDetails failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putPlanElevations = async (elevations: IPlanElevationsPut): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/plans/elevations`, elevations);
    return data;
  } catch (error) {
    logError({ error, payload: elevations }, 'putPlanElevations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPlanBrochure = async (
  planId: number | string,
  payload: IPlanBrochureCreate
): Promise<any> => {
  try {
      return await axios.post(`/api/plans/marketing/brochure/${planId}`, payload, { responseType: 'blob' });
  } catch (error) {
    logError({ error }, 'createPlanBrochure failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlanOvenLayouts = async (): Promise<IPlanOvenLayout[]> => {
  try {
    const { data } = await axios.get(`/api/plans/oven-layouts`);
    return data;
  } catch (error) {
    logError(error, 'getPlanOvenLayouts failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createCabinetLayoutDownload = async (
  planId: number | string,
  ovenLayout: string
): Promise<any> => {
  try {
      return await axios.post(`/api/plans/marketing/cabinet-layout/${planId}`, { ovenLayout }, { responseType: 'blob' });
  } catch (error) {
    let responseObj = await error.response.data.text();
    logError(error, 'createCabinetLayoutDownload failed');
    return Promise.reject(JSON.parse(responseObj));
  }
};

